import { Injectable } from '@angular/core';
import { AccessoryCharge, Charge, PaymentCartRequest, PaymentExternalData } from '@cargos/sprintpay-models';
import { PaymentCartRequestType } from '@cargos/sprintpay-models/dist/src/payment/payment-cart-request';
import { cleanDeepData } from '@cargos/sprintpay-tools';
import { Observable, map, of, switchMap } from 'rxjs';
import { ShipmentFormType } from 'src/app/modules/polar/pages/prepay/shipment-form/models/shipment-form';
import { Shipment } from '../models/shipment';
import { ApprovalFlowLevelsService } from './approval-flow-levels.service';
import { AwbLookUpService } from './awb-look-up.service';
import { ConfigService } from './config.service';
import { CurrentFacilityService } from './current-facility.service';
import { CustomerService } from './customer.service';
import { DynamicsFieldsService } from './dynamic-field.service';
import { FlightService } from './flight.service';
import { SummaryService } from './summary/summary.service';

export type PaymentCartTypes = Partial<Charge & AccessoryCharge & Shipment>;

@Injectable({
    providedIn: 'root',
})
export class PaymentCartBuilder {
    constructor(
        private approvalFlowService: ApprovalFlowLevelsService,
        private awbLookUpService: AwbLookUpService,
        private currentFacilityService: CurrentFacilityService,
        private dynamicFieldsService: DynamicsFieldsService,
        private customerService: CustomerService,
        private configService: ConfigService,
        private flightService: FlightService,
        private summaryService: SummaryService
    ) {}

    buildChargeBeforeAdd$(charge: Charge): Observable<PaymentCartRequest> {
        return this.getExternalData(charge).pipe(
            map((externalData) => {
                const paymentDataByCharge = this.getPaymentData(charge);

                return PaymentCartRequest.fromJson({
                    ...paymentDataByCharge,
                    externalData,
                });
            })
        );
    }

    buildAccessoryBeforeAdd(accessory: AccessoryCharge): Observable<PaymentCartRequest> {
        return this.getExternalData(accessory).pipe(
            map((externalData) => {
                const paymentDataByCharge = this.getPaymentData(accessory);

                return PaymentCartRequest.fromJson({
                    ...paymentDataByCharge,
                    externalData,
                });
            })
        );
    }

    buildShipmentBeforeAdd(shipment: Shipment): Observable<PaymentCartRequest> {
        return this.getExternalData(shipment).pipe(
            map((externalData) => {
                const paymentDataByCharge = this.getPaymentData(shipment);

                return PaymentCartRequest.fromJson({
                    ...paymentDataByCharge,
                    externalData,
                });
            })
        );
    }

    private getExternalData(payment: PaymentCartTypes): Observable<PaymentExternalData> {
        return this.getFlightInformationByTransactionType$(payment).pipe(
            map((flightInformation) => {
                let type: undefined | string;

                if (payment.amount && this.configService.isEnableRequestorApprovalFlow()) {
                    type = this.approvalFlowService.getTypeByAmount(payment.amount);
                }

                const payments = this.awbLookUpService.instant_current_payment;
                const commonExternalData = this.getCommonExternalData();
                const externalDataByCharge = this.getExternalDataByTypeOfCharge(payment);

                return cleanDeepData({
                    type,
                    paymentType: payment?.paymentType,
                    pickupDate: payments?.date,
                    agentInfoEmail: payment?.agentInfo,
                    facilityCode: payments?.shipmentDetails?.facilityCode,
                    ...flightInformation,
                    ...commonExternalData,
                    ...externalDataByCharge,
                });
            })
        );
    }

    getCommonExternalData(): PaymentExternalData {
        const source = this.currentFacilityService.getFacilitySource();
        const referenceNumber = this.dynamicFieldsService.getStaticFieldValueByKey('customerRef');
        const externalReferenceNum = this.dynamicFieldsService.getStaticFieldValueByKey('externalReferenceNum');
        const costCenter = this.dynamicFieldsService.getStaticFieldValueByKey('costCenter');
        const voucher = this.dynamicFieldsService.getStaticFieldValueByKey('voucher');
        const eiReferenceFileNumber = this.dynamicFieldsService.getStaticFieldValueByKey('eiReferenceFileNumber');
        const glCode = this.dynamicFieldsService.getStaticFieldValueByKey('glCode');

        return {
            source,
            referenceNumber,
            externalReferenceNum,
            costCenter,
            voucher,
            eiReferenceFileNumber,
            glCode,
        };
    }

    getExternalDataByTypeOfCharge(payment: PaymentCartTypes): PaymentExternalData {
        if ('chargeId' in payment) {
            return {
                awbId: payment.awbId,
                chargeId: payment.chargeId,
            };
        } else if (this.isShipmentPayment(payment)) {
            const {
                accountNumber,
                agentName,
                iataNumber,
                typeWeight,
                weightExternalData,
                paymentType,
                pickupDate,
                sourceExternalData: source,
                dsvVtfAccount,
                weight,
                ...dynamicFields
            } = payment;

            const { awb, hawbId, amount, ...filteredDynamicFields } = dynamicFields;

            return {
                accountNumber,
                agentName,
                iataNumber,
                typeWeight,
                weight: weightExternalData,
                paymentType,
                pickupDate,
                source,
                dsvVtfAccount,
                ...filteredDynamicFields,
            };
        }

        return {};
    }

    private getPaymentData(payment: PaymentCartTypes): PaymentCartRequestType {
        let company;
        if (this.configService.isEnableRequestorApprovalFlow()) {
            company = this.getCompanyData();
        }
        const source = this.currentFacilityService.getFacilitySource();
        const dynamicValues = this.dynamicFieldsService.getDynamicFieldsAndValues();
        const attachments = this.dynamicFieldsService.getReplacementFieldsAndValues('nameFiles');
        const mappedValues = this.configService.mapShipmentValues(payment as Record<string, unknown>);
        const paymentDataByCharge = this.getPaymentDataByTypeOfCharge(payment);
        const customerReference = this.getCustomerReference();
        const isAccessorial = this.isAccessorialCharge(payment);
        const facility = this.currentFacilityService.getPaidToFacilityId();

        return cleanDeepData({
            amount: payment.amount,
            hawb: payment.hawbId,
            paymentType: payment.paymentType,
            agentInfo: payment.agentInfo,
            customerReference,
            attachments,
            source,
            company,
            isAccessorial,
            facility,
            dynamicValues: dynamicValues as unknown as Record<string, string>[],
            ...paymentDataByCharge,
            ...mappedValues,
        });
    }

    private getPaymentDataByTypeOfCharge(payment: PaymentCartTypes): {
        awb?: string | undefined;
        origin?: string | undefined;
        destination?: string | undefined;
        arrivalDate?: string | undefined;
        attachments?: string | undefined;
    } {
        const shipmentInformation = this.getShipmentInformation();
        if (this.isAccessorialCharge(payment)) {
            return {
                awb: shipmentInformation?.awb,
                origin: shipmentInformation?.origin,
                destination: shipmentInformation?.destination || payment.destinations,
                arrivalDate: shipmentInformation?.arrivalDate,
            };
        } else if ('awbId' in payment) {
            return {
                awb: shipmentInformation?.awb || payment.awbId,
                origin: shipmentInformation?.origin,
                destination: shipmentInformation?.destination || payment.destination,
                arrivalDate: shipmentInformation?.arrivalDate,
            };
        } else if (this.isShipmentPayment(payment)) {
            return {
                awb: payment.awb,
                destination: payment.destination,
                origin: payment.origin,
                attachments: payment.attachments,
            };
        }

        return {
            awb: shipmentInformation?.awb,
            origin: shipmentInformation?.origin,
            destination: shipmentInformation?.destination,
            arrivalDate: shipmentInformation?.arrivalDate,
        };
    }

    private isAccessorialCharge(charge: PaymentCartTypes): boolean | undefined {
        return charge.isAccessorial;
    }

    private getCompanyData():
        | {
              id: string;
          }
        | undefined {
        const companyId = this.customerService.getCompanyId()?.toString();

        if (companyId) {
            return { id: companyId };
        }

        return undefined;
    }

    private getCustomerReference(): string | undefined {
        const customerReference = this.dynamicFieldsService.getStaticFieldValueByKey('customerRef') || undefined;

        return customerReference;
    }

    private getShipmentInformation(): {
        awb?: string;
        origin?: string;
        destination?: string;
        arrivalDate?: string;
    } {
        const currentPayment = this.awbLookUpService.instant_current_payment;

        return {
            awb: currentPayment?.awb,
            origin: currentPayment?.shipmentDetails?.origin,
            destination: currentPayment?.shipmentDetails?.destination,
            arrivalDate: currentPayment?.shipmentDetails?.arrivalDate,
        };
    }

    private getFlightInformationByTransactionType$(payment: PaymentCartTypes): Observable<{
        flightNumber?: string;
        flightDate?: string;
    }> {
        if (this.isAccessorialPayment(payment)) {
            return this.getFlightInformationToAccessory$(payment);
        }

        return this.getFlightInformationToCharge$(payment as Charge);
    }

    private getFlightInformationToCharge$(charge: Charge): Observable<{ flightNumber?: string; flightDate?: string }> {
        if (this.configService.logicBaseOnFlightNumberAndFlightDate()) {
            return this.flightService.getFlightNumberAndFlightDateBySearchTypeAndUniqueId$(charge.uniqueId);
        }
        if (this.configService.isEnableFlightDateInRequest()) {
            return of({
                flightDate: this.awbLookUpService.getFlightDate(),
            });
        }
        return of({});
    }

    private getFlightInformationToAccessory$(
        accessory: AccessoryCharge
    ): Observable<{ flightNumber?: string; flightDate?: string }> {
        if (this.configService.logicBaseOnFlightNumberAndFlightDate()) {
            if (accessory.flightNumber) {
                return of({
                    flightNumber: accessory?.flightNumber || '',
                    flightDate: accessory?.flightDate || '',
                });
            }

            return this.summaryService.getFlightActive().pipe(
                switchMap((flightActive) => {
                    return this.flightService.getFlightNumberAndFlightDateBySearchTypeAndFlightInfo$(
                        flightActive?.flightNumber,
                        flightActive?.flightDate
                    );
                })
            );
        }
        if (this.configService.isEnableFlightDateInRequest()) {
            return of({
                flightDate: this.awbLookUpService.getFlightDate(),
            });
        }
        return of({} as { flightNumber?: string; flightDate?: string });
    }

    private isShipmentPayment(shipment: PaymentCartTypes | ShipmentFormType | Shipment): boolean {
        return (
            'agentInfo' in shipment ||
            ('iataNumber' in shipment && 'agentName' in shipment) ||
            shipment.destination === 'bdp' ||
            shipment.destination === 'sprintpay'
        );
    }

    private isAccessorialPayment(payment: PaymentCartTypes | ShipmentFormType | Shipment): boolean {
        return 'isAccessorial' in payment && !!payment.isAccessorial;
    }
}
